import { EventsClient } from "./clients";
import {
  FabriqEventPayload,
  SearchEventPayload,
  FabriqEvent,
  ListService,
  FabriqEventFromServer,
  FabriqFile,
} from "@/types";
import { FileSizeExeededError, getFormData } from "./files";

function isFabriqEvent(event: FabriqEvent | unknown): event is FabriqEvent {
  if (typeof event !== "object" || !event) {
    return false;
  }
  if ("outcome" in event) {
    return false;
  }
  return (event as FabriqEvent).id !== undefined;
}

export interface EventsListService extends ListService<FabriqEvent> {
  postEventFile: (url: string) => Promise<
    | {
        outcome: "uploaded";
        fileId: string;
      }
    | {
        outcome: "notUploaded";
        reason: "maximumFileSizeExeeded" | "apiError";
      }
  >;
  fetchEventFile: (
    fileId: string
  ) => Promise<{ outcome: "fetched"; url: string } | { outcome: "notFetched" }>;
  fetchAll: (
    options: SearchEventPayload
  ) => Promise<{ events: FabriqEventFromServer[]; count: number | undefined }>;
}

const uploadAndMapFiles = async (payload: FabriqEventPayload) => {
  const files = await Promise.all(
    payload.files.map(async (file) => {
      if (file.media_type === "url") {
        return file;
      }
      if (!file.tmpUrl) return file;
      const result = await EventsService.postEventFile(file.tmpUrl);
      if (result.outcome === "uploaded") {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { tmpUrl, tmpName, ...filePayload } = file;
        return { ...filePayload, key: result.fileId };
      }
      return undefined;
    })
  );
  return files.filter((f) => f) as FabriqFile[];
};
export const EventsService: EventsListService = {
  async add(payload: FabriqEventPayload, request): Promise<FabriqEvent> {
    const files = await uploadAndMapFiles(payload);
    payload.files = files;
    const result = await EventsClient.post<
      | FabriqEvent
      | {
          outcome: "notUpdated";
          reason: string;
        }
    >(
      `/event-type/${payload.eventTypeId}/events`,
      {
        eventTypeId: payload.eventTypeId,
        properties: payload.properties,
        files: payload.files,
      },
      false,
      request
    );

    if (!isFabriqEvent(result)) throw new Error("eventNotCreated");

    return result;
  },
  async update(payload: FabriqEventPayload, request): Promise<FabriqEvent> {
    try {
      const files = await uploadAndMapFiles(payload);
      payload.files = files;
      const result = await EventsClient.put<
        | FabriqEvent
        | {
            outcome: "notUpdated";
            reason: string;
          }
      >(
        `/${payload.id}`,
        {
          id: payload.id,
          eventTypeId: payload.eventTypeId,
          isComplete: false,
          files: payload.files,
          properties: payload.properties,
        },
        request
      );

      if (!isFabriqEvent(result)) throw new Error("eventNotUpdated");

      return result;
    } catch (error: any) {
      console.error(error);
      throw new Error("eventNotUpdated");
    }
  },
  async remove(evt: FabriqEvent, request): Promise<void> {
    await EventsClient.delete(`/${evt.id}`, request);
  },
  async fetchAll(
    options: SearchEventPayload
  ): Promise<{ events: FabriqEventFromServer[]; count: number | undefined }> {
    try {
      const {
        eventTypeId,
        filters,
        isComplete,
        page,
        asc,
        sortProperty,
        teamId,
      } = options;
      const params = new URLSearchParams();
      params.append("page", page?.toString() || "0");
      params.append("asc", asc ? "1" : "0");

      let events;
      if (eventTypeId) {
        events = await EventsClient.post<{
          events: FabriqEventFromServer[];
          count: number | undefined;
        }>(`/event-type/${eventTypeId}/filter-events?${params.toString()}`, {
          filters,
          isComplete,
          ...(sortProperty ? { sortEventTypeProperty: sortProperty } : {}),
          ...(teamId ? { teamId } : {}),
        });
      } else {
        events = await EventsClient.post<{
          events: FabriqEventFromServer[];
          count: number | undefined;
        }>(`/filter-events?${params.toString()}`, {
          filters,
          isComplete,
          ...(sortProperty ? { sortEventTypeProperty: sortProperty } : {}),
          ...(teamId ? { teamId } : {}),
        });
      }
      if (!events) throw new Error("eventsNotFetched");
      return events;
    } catch (error) {
      console.error(error);
      throw new Error("eventNotFetched");
    }
  },
  async load(id: string): Promise<FabriqEventFromServer | undefined> {
    try {
      const result = await EventsClient.get<FabriqEventFromServer | undefined>(
        `/${id}`
      );
      if (!result) throw new Error("eventNotFetched");
      return result;
    } catch (error) {
      console.error(error);
      throw new Error("eventNotFetched");
    }
  },

  async postEventFile(url: string): Promise<
    | {
        outcome: "uploaded";
        fileId: string;
      }
    | {
        outcome: "notUploaded";
        reason: "maximumFileSizeExeeded" | "apiError";
      }
  > {
    try {
      const formData = await getFormData(url, "file");
      const { key } = await EventsClient.sendFile(`/file`, formData);

      if (!key) return { outcome: "notUploaded", reason: "apiError" };
      return { outcome: "uploaded", fileId: key };
    } catch (error) {
      if (error instanceof FileSizeExeededError) {
        return { outcome: "notUploaded", reason: "maximumFileSizeExeeded" };
      }
      console.error(error);
      return { outcome: "notUploaded", reason: "apiError" };
    }
  },
  async fetchEventFile(fileId: string): Promise<
    | {
        outcome: "fetched";
        url: string;
      }
    | {
        outcome: "notFetched";
      }
  > {
    try {
      const response = await EventsClient.get<{ path?: string }>(
        `/file?key=${fileId}`
      );
      if (!response) return { outcome: "notFetched" };
      const { path } = response;
      if (!path) return { outcome: "notFetched" };
      return { outcome: "fetched", url: path };
    } catch (error) {
      console.error(error);
      return { outcome: "notFetched" };
    }
  },
};
